import { create } from 'zustand';
import { transphereService, type TransphereStatusDto } from '../services/transphereService';
import { useTenantStore } from './tenantStore';

interface TransphereStatusState {
    status: TransphereStatusDto | null;
    // True once a fetch has settled for the current tenant, success or failure. Both outcomes can
    // leave `status` null, so this is what tells "no answer yet" from "answered, no data".
    resolved: boolean;
    // tenantSwitchedAt the current status/resolved reflect; null before any fetch has ever started.
    // A mismatch against the live tenantStore value is what tells fetchStatus a fetch is due.
    fetchedTenantSwitchedAt: number | null;
    // Carries the tenant it belongs to: a request started before a tenant switch must not satisfy a
    // caller asking about the tenant switched to.
    inFlight: { tenantSwitchedAt: number; promise: Promise<void> } | null;
    // Fetches at most once per tenant, deduping concurrent callers onto the same in-flight request.
    fetchStatus: () => Promise<void>;
    // Re-reads past the once-per-tenant guard, floored so the consumers sharing this store cost one
    // call between them.
    revalidate: () => Promise<void>;
    // Lets the next revalidate through the floor.
    expectChange: () => void;
}

// `focus` fires on every click into the iframe, and one return often lands two signals.
const REVALIDATE_INTERVAL_MS = 30_000;

let lastRevalidatedAt = 0;
let revalidating = false;

export const useTransphereStatusStore = create<TransphereStatusState>((set, get) => ({
    status: null,
    resolved: false,
    fetchedTenantSwitchedAt: null,
    inFlight: null,

    fetchStatus: async () => {
        const tenantSwitchedAt = useTenantStore.getState().tenantSwitchedAt;
        const { fetchedTenantSwitchedAt, inFlight } = get();
        if (fetchedTenantSwitchedAt === tenantSwitchedAt) return;
        if (inFlight?.tenantSwitchedAt === tenantSwitchedAt) return inFlight.promise;

        // Invalidate before awaiting, not on settle: otherwise a fail-open consumer reads the previous
        // tenant's "not subscribed" as current for the length of the request.
        set({ status: null, resolved: false });

        const isStale = () => useTenantStore.getState().tenantSwitchedAt !== tenantSwitchedAt;

        const promise = (async () => {
            try {
                const status = await transphereService.getStatus(true);
                if (isStale()) return;
                set({ status, resolved: true, fetchedTenantSwitchedAt: tenantSwitchedAt });
            } catch (e) {
                console.error('Failed to fetch Transphere status:', e);
                if (isStale()) return;
                set({ status: null, resolved: true, fetchedTenantSwitchedAt: tenantSwitchedAt });
            } finally {
                if (get().inFlight?.tenantSwitchedAt === tenantSwitchedAt) set({ inFlight: null });
            }
        })();

        set({ inFlight: { tenantSwitchedAt, promise } });
        return promise;
    },

    expectChange: () => { lastRevalidatedAt = 0; },

    revalidate: async () => {
        if (revalidating || Date.now() - lastRevalidatedAt < REVALIDATE_INTERVAL_MS) return;

        const tenantSwitchedAt = useTenantStore.getState().tenantSwitchedAt;
        if (get().fetchedTenantSwitchedAt !== tenantSwitchedAt) return void get().fetchStatus();

        lastRevalidatedAt = Date.now();
        revalidating = true;
        try {
            const status = await transphereService.getStatus(true);
            // Unlike fetchStatus nothing is blanked first, and a failure below changes nothing: a
            // re-check must not flicker a resolved answer nor retract what is on screen.
            if (useTenantStore.getState().tenantSwitchedAt === tenantSwitchedAt) {
                set({ status, resolved: true, fetchedTenantSwitchedAt: tenantSwitchedAt });
            }
        } catch (e) {
            console.error('Failed to re-read the Transphere status:', e);
        } finally {
            revalidating = false;
        }
    },
}));
